import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  containerClassName?: string
  headerClassName?: string
  titleClassName?: string
  closeButtonClassName?: string
}

const SIZE_CLASSES = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
}

export default function Modal({
  isOpen,
  onClose,
  children,
  title,
  size = 'md',
  containerClassName = 'bg-black/40 backdrop-blur-md border border-white/10 rounded-xl shadow-2xl',
  headerClassName = 'border-b border-white/10',
  titleClassName = 'text-xl font-bold text-white',
  closeButtonClassName = 'text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors',
}: ModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const closeBtnRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
      if (e.key === 'Tab' && containerRef.current) {
        const focusable = containerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault()
            ;(last as HTMLElement).focus()
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault()
            ;(first as HTMLElement).focus()
          }
        }
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKey)
      document.body.style.overflow = 'hidden'
      setTimeout(() => closeBtnRef.current?.focus(), 0)
    }

    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const labelledById = title ? 'modal-title' : undefined

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledById}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            ref={containerRef}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className={`relative w-full ${SIZE_CLASSES[size]} max-h-[90vh] overflow-hidden ${containerClassName}`}
          >
            <div className={`flex items-center justify-between p-6 ${headerClassName}`}>
              {title && (
                <h2 id={labelledById} className={titleClassName}>
                  {title}
                </h2>
              )}
              <button
                ref={closeBtnRef}
                onClick={onClose}
                className={closeButtonClassName}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
